import { type ReactNode, useState, useRef, useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";

import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { useAuth } from "@/providers/AuthProvider";
import { isMobileBrowser } from "@/utils/mobile";
import MobileWalletSheet from "@/components/MobileWalletSheet";
import { Icon } from "@/components/ui";
import Button from "@/components/ui/Button";

/**
 * T2.2 — Wallet connect/disconnect button.
 *
 * States:
 *  1. No wallet → "Connect" button (triggers Phantom connect)
 *  2. Connected/authenticated → Truncated address with dropdown (sign out)
 *
 * Variants:
 *  - "legacy" (default): Small pill button for old-design pages/TopBar
 *  - "cta": Full-width white CTA for redesigned pages (e.g. WalletPage)
 */

interface WalletButtonProps {
    variant?: "legacy" | "cta";
}

export default function WalletButton({ variant = "legacy" }: WalletButtonProps): ReactNode {
    const { publicKey, connected } = useWallet();
    const { setVisible } = useWalletModal();
    const { user, isAuthenticated, signOut, loading } = useAuth();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node)
            ) {
                setDropdownOpen(false);
            }
        }
        if (dropdownOpen) document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [dropdownOpen]);

    const truncateAddress = (addr: string): string => {
        return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
    };

    const handleConnect = () => {
        if (isMobileBrowser()) {
            setMobileSheetOpen(true);
            return;
        }
        setVisible(true);
    };

    const mobileSheet = (
        <MobileWalletSheet
            open={mobileSheetOpen}
            onClose={() => setMobileSheetOpen(false)}
        />
    );

    // ─── CTA variant (redesign v2) ───
    if (variant === "cta") {
        // State 1: Not connected
        if (!connected) {
            return (
                <>
                    <Button
                        id="wallet-connect-btn"
                        variant="cta"
                        size="inline"
                        textStyle="v2"
                        onClick={handleConnect}
                    >
                        Connect Wallet
                        <Icon name="wallet-default" size={20} className="" />
                    </Button>
                    {mobileSheet}
                </>
            );
        }

        // State 2: Connected (authenticating or authenticated)
        if (loading) {
            return (
                <Button
                    variant="cta"
                    size="lg"
                    textStyle="v2"
                    fullWidth
                    loading
                    disabled
                    className="max-w-[400px] h-[clamp(44px,5.5dvh,52px)] text-[16px]"
                >
                    Connecting...
                </Button>
            );
        }

        // Authenticated — nothing to render (WalletPage shows the yellow card)
        return null;
    }

    // ─── Legacy variant (old design) ───

    // State 1: No wallet connected → Connect
    if (!connected) {
        return (
            <>
                <button
                    id="wallet-connect-btn"
                    onClick={handleConnect}
                    className="flex items-center gap-1.5 bg-accent-lime text-background-dark px-3 py-1.5 rounded-full text-[10px] font-black uppercase italic tracking-tight cta-neon-lime active:scale-95 transition-transform"
                >
                    <Icon name="wallet" size={14} />
                    Connect
                </button>
                {mobileSheet}
            </>
        );
    }

    // State 2: Connected but still authenticating
    if (!isAuthenticated) {
        return (
            <button
                disabled
                className="flex items-center gap-1.5 bg-accent-lime text-background-dark px-3 py-1.5 rounded-full text-[10px] font-black uppercase italic tracking-tight opacity-50 cursor-wait"
            >
                <Icon name="spinner" size={14} spinning />
                Connecting...
            </button>
        );
    }

    // State 3: Authenticated → Show address + dropdown
    return (
        <div ref={dropdownRef} className="relative">
            <button
                id="wallet-address-btn"
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-accent-lime/30 text-accent-lime px-3 py-1.5 rounded-full text-[10px] font-black uppercase italic tracking-tight transition-colors"
            >
                <span className="w-2 h-2 rounded-full bg-accent-lime shadow-[0_0_6px_rgba(204,255,0,0.8)]" />
                {user?.displayName || truncateAddress(publicKey!.toBase58())}
                <Icon name={dropdownOpen ? "expand-less" : "expand-more"} size={14} />
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-card-dark border border-white/10 rounded-lg overflow-hidden shadow-xl z-[60] animate-[slideDown_0.2s_ease-out]">
                    {/* Full address */}
                    <div className="px-4 py-3 border-b border-white/10">
                        <p className="text-[9px] uppercase font-bold text-white/30 tracking-widest mb-1">
                            Wallet
                        </p>
                        <p className="text-xs font-mono text-white/80 break-all">
                            {publicKey!.toBase58()}
                        </p>
                    </div>

                    {/* Copy address */}
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(publicKey!.toBase58());
                            setDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-xs text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                    >
                        <Icon name="copy" size={14} />
                        Copy Address
                    </button>

                    {/* Sign out */}
                    <button
                        onClick={() => {
                            signOut();
                            setDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-xs text-red-400 hover:bg-red-500/5 transition-colors border-t border-white/5"
                    >
                        <Icon name="disconnect" size={14} />
                        Sign Out
                    </button>
                </div>
            )}
        </div>
    );
}
